import { RefObject, useEffect, useRef } from 'react';

import { useRouter, type RegisteredRouter } from '@tanstack/react-router';

import { useGlobalModalsContext } from '@/components/layoutAndProvidersWrapper/globalModalsProvider/index.ts';
import type { FileRouteTypes } from '@/routeTree.gen.ts';
import { useLayoutStorage } from '@/stores';

import {
  chordSecondaryMatches,
  HOTKEY_CHORD_TIMEOUT_MS,
  type PendingHotkeyChord,
} from './hotkeyChordHelpers.ts';
import {
  clearTimeoutFromRef,
  hotkeyPrimaryOrOptionalCodeMatches,
  isInside,
  isTypingTarget,
  keyboardModifiersMatchHotkeyBase,
} from './hotkeyFunctions.ts';
import type { Hotkey } from './hotkeys';
import { GlobalHotkeyName } from './hotkeys/types.ts';
import { ROUTE_TO } from './useGlobalHotkeys/routesTo.ts';
import { useGlobalHotkeysDefinitions } from './useHotkeyDefinitions/useGlobalHotkeysDefinitions.tsx';

/**
 * Global navigation shortcuts (modifier + key from `useGlobalHotkeysDefinitions()`).
 * Call from a component under `RouterProvider` (`LayoutAndProvidersWrapper`).
 * Uses matchRoute(..., { fuzzy: false }) so `/layouts/:id` is not treated as the layouts index `/layouts`.
 */

type UseGlobalShortcutsProps = {
  /** Shell element for `isInside` / keyboard scope (wrap routed content). */
  scopeRef: RefObject<HTMLElement | null>;
};

const navigateUnlessCurrent = (router: RegisteredRouter, to: FileRouteTypes['to']) => {
  if (router.matchRoute({ to }, { fuzzy: false })) return;
  router.navigate({ to });
};

const navigateDashboardFromShortcut = (router: RegisteredRouter) => {
  const { currentLayoutId, getCurrentLayoutFromStorage } = useLayoutStorage.getState();
  const hasValidCurrentLayout =
    Boolean(currentLayoutId) &&
    currentLayoutId !== '' &&
    getCurrentLayoutFromStorage() !== undefined;

  if (hasValidCurrentLayout) {
    if (
      router.matchRoute({ to: '/layouts/$id', params: { id: currentLayoutId } }, { fuzzy: false })
    ) {
      return;
    }
    router.navigate({ to: '/layouts/$id', params: { id: currentLayoutId } });
    return;
  }

  navigateUnlessCurrent(router, ROUTE_TO.layouts);
};

export const useGlobalHotkeys = ({ scopeRef }: UseGlobalShortcutsProps) => {
  const router = useRouter();
  const { showGlobalShortcutsModal } = useGlobalModalsContext();
  const globalHotkeyGroups = useGlobalHotkeysDefinitions();

  const pendingChordRef = useRef<PendingHotkeyChord<GlobalHotkeyName> | null>(null);
  const chordTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const globalHotkeys = Object.assign({}, ...globalHotkeyGroups) as Record<
      GlobalHotkeyName,
      Hotkey
    >;

    const handleHotkeyName = (name: GlobalHotkeyName) => {
      switch (name) {
        case GlobalHotkeyName.ShowKeyboardShortcutsModalKey:
          showGlobalShortcutsModal();
          return;
        case GlobalHotkeyName.DashboardPageKey:
          navigateDashboardFromShortcut(router);
          return;
      }

      let to: FileRouteTypes['to'] | undefined;
      switch (name) {
        case GlobalHotkeyName.LayoutsPageKey:
          to = ROUTE_TO.layouts;
          break;
        case GlobalHotkeyName.BargeControlsPageKey:
          to = ROUTE_TO.bargeControl;
          break;
        case GlobalHotkeyName.CameraControlsPageKey:
          to = ROUTE_TO.cameraControl;
          break;
        case GlobalHotkeyName.HybridPowerPageKey:
          to = ROUTE_TO.hybridPower;
          break;
        case GlobalHotkeyName.OrbitOnePageKey:
          to = ROUTE_TO.orbitOne;
          break;
        case GlobalHotkeyName.PenViewerPageKey:
          to = ROUTE_TO.penViewer;
          break;
        case GlobalHotkeyName.AutomationPageKey:
          to = ROUTE_TO.utilsAutomation;
          break;
        case GlobalHotkeyName.UpCyclePageKey:
          to = ROUTE_TO.upCycle;
          break;
        case GlobalHotkeyName.DeviceConfigurationPageKey:
          to = ROUTE_TO.utilsDeviceConfig;
          break;
        case GlobalHotkeyName.EventStreamPageKey:
          to = ROUTE_TO.utilsEventStream;
          break;
        case GlobalHotkeyName.InventoryPageKey:
          to = ROUTE_TO.utilsInventory;
          break;
        case GlobalHotkeyName.SettingsPageKey:
          to = ROUTE_TO.settings;
          break;
      }

      if (to !== undefined) {
        navigateUnlessCurrent(router, to);
      }
    };

    const onKeyDown = (ev: KeyboardEvent) => {
      if (!isInside(scopeRef)) return;
      if (isTypingTarget(ev.target)) return;

      const pending = pendingChordRef.current;
      if (pending && chordSecondaryMatches(ev, pending)) {
        ev.preventDefault();
        clearTimeoutFromRef(chordTimeoutRef);
        pendingChordRef.current = null;
        handleHotkeyName(pending.name);
        return;
      }

      if (pending && !chordSecondaryMatches(ev, pending)) {
        clearTimeoutFromRef(chordTimeoutRef);
        pendingChordRef.current = null;
      }

      for (const [name, hotkey] of Object.entries(globalHotkeys) as [GlobalHotkeyName, Hotkey][]) {
        if (hotkey.disabled) continue;
        if (!hotkeyPrimaryOrOptionalCodeMatches(hotkey, ev.code)) continue;
        if (!keyboardModifiersMatchHotkeyBase(ev, hotkey)) continue;

        ev.preventDefault();

        if (hotkey.secondary) {
          pendingChordRef.current = { name, hotkey };
          clearTimeoutFromRef(chordTimeoutRef);
          chordTimeoutRef.current = setTimeout(() => {
            pendingChordRef.current = null;
            chordTimeoutRef.current = undefined;
          }, HOTKEY_CHORD_TIMEOUT_MS);
          return;
        }

        handleHotkeyName(name);
        return;
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      clearTimeoutFromRef(chordTimeoutRef);
      pendingChordRef.current = null;
    };
  }, [router, scopeRef, showGlobalShortcutsModal, globalHotkeyGroups]);
};
